/**
 *  @class
 *  @function Pagination
 */

if (!customElements.get('pagination')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector('[data-id=' + id + ']');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.i = 1; // Initialize to 1 so first increment makes it 2
      this.fillingRow = false; // Flag to prevent infinite loops
      this.preloadedProducts = new Set(); // Track product IDs that were pre-loaded to fill rows
      this.pageSkipCount = {}; // Track how many products to skip per page (for pre-loaded products)
      this.partialPageLoaded = null; // Track which page was partially loaded
      this.productsPerPage = this.dataset.productsPerPage ? parseInt(this.dataset.productsPerPage, 10) : null;
      this.totalPages = this.dataset.totalPages ? parseInt(this.dataset.totalPages, 10) : null;
      this.currentPage = this.dataset.currentPage ? parseInt(this.dataset.currentPage, 10) : 1;
      this.nextUrl = this.dataset.nextUrl || null; // Initialize nextUrl from dataset
      this.allProductsLoaded = false; // Flag to track if all products have been loaded
      this.autoPreloadLimit = this.dataset.autoPreloadPages ? parseInt(this.dataset.autoPreloadPages, 10) : 3;
      this.autoPreloadCount = 0;
      this.lastLoadTriggeredByButton = false;
      
      console.log('[Pagination] Initialized with totalPages:', this.totalPages, 'currentPage:', this.currentPage, 'nextUrl:', this.nextUrl);
    }
    getColumnsDesktop() {
      // Extract columns_desktop from grid class (e.g., "medium-up-4" -> 4)
      if (!this.grid) {
        // Try to get grid again if not found
        const id = this.dataset.section;
        this.grid = document.querySelector('[data-id=' + id + ']');
      }
      if (!this.grid) return 4; // default fallback
      const classList = this.grid.className.split(' ');
      for (let i = 0; i < classList.length; i++) {
        const match = classList[i].match(/medium-up-(\d+)/);
        if (match) {
          return parseInt(match[1], 10);
        }
      }
      return 4; // default fallback
    }
    getCurrentProductCount() {
      if (!this.grid) return 0;
      return this.getProductColumns().length;
    }
    getProductColumns() {
      if (!this.grid) return [];
      return Array.from(this.grid.querySelectorAll(':scope > .column:not(.promotion-column)')).filter(function(el) {
        return !!el.querySelector('.product-card');
      });
    }
    getPromoInterval() {
      if (!this.grid) return 0;
      return parseInt(this.grid.dataset.promoInterval, 10) || 0;
    }
    isPromoRepeatEnabled() {
      if (!this.grid) return true;
      return this.grid.dataset.promoRepeat !== 'false';
    }
    getPromoConfigs() {
      if (!this.grid) return [];

      // Prefer template data attributes (avoids JSON parse / escape issues)
      const sectionId = this.dataset.section;
      const templateEl = document.getElementById('collection-grid-promo-template-' + sectionId);
      if (templateEl && templateEl.content) {
        const templatePromos = Array.from(templateEl.content.querySelectorAll('.promotion-column'));
        if (templatePromos.length) {
          return templatePromos.map(function(el) {
            return {
              id: el.getAttribute('data-promo-block-id'),
              position: parseInt(el.getAttribute('data-promo-position'), 10) || 0,
              showOnce: el.getAttribute('data-promo-show-once') === 'true',
              afterGrid: el.getAttribute('data-promo-after-grid') === 'true'
            };
          }).filter(function(config) {
            return config.position > 0;
          });
        }
      }

      const raw = this.grid.dataset.promoConfigs;
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length) {
            return parsed.map(function(config) {
              return {
                id: config.id,
                position: parseInt(config.position, 10) || 0,
                showOnce: !!config.showOnce,
                afterGrid: !!config.afterGrid
              };
            }).filter(function(config) {
              return config.position > 0;
            });
          }
        } catch (e) {
          console.warn('[Pagination] Failed to parse promo configs', e);
        }
      }

      const interval = this.getPromoInterval();
      if (!interval) return [];
      return [{
        id: null,
        position: interval,
        showOnce: !this.isPromoRepeatEnabled(),
        afterGrid: false
      }];
    }
    shouldPlacePromoAtSlot(slot, config, showOncePlaced) {
      if (!config || !config.position || slot < 1) return false;
      if (config.showOnce) {
        if (showOncePlaced && config.id && showOncePlaced.has(config.id)) return false;
        return slot === config.position;
      }
      if (config.position === 1) return true;
      return slot % config.position === 0;
    }
    collectPromosForSlot(slot, configs, showOncePlaced, afterGrid) {
      const promos = [];
      if (afterGrid) {
        for (let c = 0; c < configs.length; c++) {
          const config = configs[c];
          if (!config.afterGrid) continue;
          if (this.shouldPlacePromoAtSlot(slot, config, showOncePlaced)) {
            promos.push(config);
            if (config.showOnce && config.id) showOncePlaced.add(config.id);
          }
        }
        return { promos: promos, slot: slot };
      }

      let currentSlot = slot;
      let placed = true;
      while (placed) {
        placed = false;
        for (let c = 0; c < configs.length; c++) {
          const config = configs[c];
          if (config.afterGrid) continue;
          if (this.shouldPlacePromoAtSlot(currentSlot, config, showOncePlaced)) {
            promos.push(config);
            if (config.showOnce && config.id) showOncePlaced.add(config.id);
            currentSlot++;
            placed = true;
            break;
          }
        }
      }
      return { promos: promos, slot: currentSlot };
    }
    buildPromoPlacementPlan(productCount, configs) {
      const plan = [];
      const trailing = [];
      if (!configs.length || productCount < 1) {
        return { plan: plan, trailing: trailing };
      }

      let slot = 1;
      let productIndex = 0;
      const showOncePlaced = new Set();

      while (productIndex < productCount) {
        const beforeResult = this.collectPromosForSlot(slot, configs, showOncePlaced, false);
        slot = beforeResult.slot;
        const cardNumber = productIndex + 1;
        productIndex++;
        slot++;
        const afterResult = this.collectPromosForSlot(cardNumber, configs, showOncePlaced, true);
        slot += afterResult.promos.length;

        if (beforeResult.promos.length || afterResult.promos.length) {
          plan.push({
            productIndex: productIndex - 1,
            before: beforeResult.promos,
            after: afterResult.promos,
            promos: beforeResult.promos
          });
        }
      }

      const placedAfterIds = new Set();
      for (let p = 0; p < plan.length; p++) {
        const afterPromos = plan[p].after || [];
        for (let a = 0; a < afterPromos.length; a++) {
          if (afterPromos[a].id) placedAfterIds.add(afterPromos[a].id);
        }
      }

      let trailingPlaced = true;
      while (trailingPlaced) {
        trailingPlaced = false;
        for (let c = 0; c < configs.length; c++) {
          const config = configs[c];
          if (!config.afterGrid || !config.position) continue;
          if (config.id && (showOncePlaced.has(config.id) || placedAfterIds.has(config.id))) continue;
          const matchesSlot = slot === config.position;
          const matchesLastCard = config.position >= productCount;
          if (!matchesSlot && !matchesLastCard) continue;
          trailing.push(config);
          if (config.id) {
            showOncePlaced.add(config.id);
            placedAfterIds.add(config.id);
          }
          slot++;
          trailingPlaced = true;
          break;
        }
      }

      return { plan: plan, trailing: trailing };
    }
    countPromosForProducts(productCount) {
      const configs = this.getPromoConfigs();
      if (!configs.length || productCount < 1) return 0;
      const placement = this.buildPromoPlacementPlan(productCount, configs);
      let count = 0;
      const plan = placement.plan || [];
      for (let i = 0; i < plan.length; i++) {
        count += (plan[i].before || plan[i].promos || []).length;
        count += (plan[i].after || []).length;
      }
      count += (placement.trailing || []).length;
      return count;
    }
    getGridSlotCountForProducts(productCount) {
      return productCount + this.countPromosForProducts(productCount);
    }
    isLastRowComplete() {
      if (!this.grid) return true;
      const slotCount = this.grid.querySelectorAll(':scope > .column').length;
      const columns = this.getColumnsDesktop();
      return slotCount % columns === 0;
    }
    getProductsNeededToFillRow() {
      const productCount = this.getCurrentProductCount();
      const columns = this.getColumnsDesktop();

      if (productCount < 1) return 0;

      // Priority 1: If total product cards < products_per_page, fill to products_per_page
      if (this.productsPerPage && productCount < this.productsPerPage) {
        return this.productsPerPage - productCount;
      }

      // Priority 2: complete the last visual row (product cards + promotion columns)
      for (let add = 0; add <= columns * 4; add++) {
        if (this.getGridSlotCountForProducts(productCount + add) % columns === 0) {
          return add;
        }
      }
      return 0;
    }
    getProductId(productElement) {
      if (productElement.classList.contains('promotion-column')) {
        return null;
      }

      const variantEl = productElement.querySelector('[data-variant-id]');
      if (variantEl) {
        return 'variant:' + variantEl.getAttribute('data-variant-id');
      }

      return productElement.getAttribute('data-product-id') ||
             productElement.getAttribute('data-id') ||
             (productElement.querySelector('a') ? productElement.querySelector('a').getAttribute('href') : null) ||
             productElement.innerHTML.substring(0, 100);
    }
    repositionPromotionColumns() {
      if (!this.grid) return;

      const configs = this.getPromoConfigs();
      if (!configs.length) return;

      const sectionId = this.dataset.section;
      const templateEl = document.getElementById('collection-grid-promo-template-' + sectionId);
      const templateRoot = templateEl && templateEl.content ? templateEl.content : null;
      const templatePromos = templateRoot
        ? Array.from(templateRoot.querySelectorAll('.promotion-column'))
        : [];
      const existingPromos = Array.from(this.grid.querySelectorAll(':scope > .promotion-column'));
      const products = this.getProductColumns();
      if (!products.length) return;

      const resolvePromoSource = function(config, configIndex) {
        let promoSource = null;
        if (config.id) {
          for (let t = 0; t < templatePromos.length; t++) {
            if (templatePromos[t].getAttribute('data-promo-block-id') === config.id) {
              promoSource = templatePromos[t];
              break;
            }
          }
          if (!promoSource) {
            for (let e = 0; e < existingPromos.length; e++) {
              if (existingPromos[e].getAttribute('data-promo-block-id') === config.id) {
                promoSource = existingPromos[e];
                break;
              }
            }
          }
        }
        if (!promoSource && templatePromos[configIndex]) promoSource = templatePromos[configIndex];
        if (!promoSource && existingPromos[configIndex]) promoSource = existingPromos[configIndex];
        if (!promoSource && templatePromos[0]) promoSource = templatePromos[0];
        if (!promoSource && existingPromos[0]) promoSource = existingPromos[0];
        return promoSource;
      };

      const placement = this.buildPromoPlacementPlan(products.length, configs);
      const plan = placement.plan || [];
      const trailing = placement.trailing || [];
      const insertsBefore = {};
      const insertsAfter = {};
      const trailingNodes = [];

      const clonePromo = function(config) {
        const configIndex = configs.indexOf(config);
        const promoSource = resolvePromoSource(config, configIndex);
        return promoSource ? promoSource.cloneNode(true) : null;
      };

      plan.forEach(function(entry) {
        const productIndex1 = entry.productIndex + 1;
        (entry.before || entry.promos || []).forEach(function(config) {
          const node = clonePromo(config);
          if (!node) return;
          if (!insertsBefore[productIndex1]) insertsBefore[productIndex1] = [];
          insertsBefore[productIndex1].push(node);
        });
        (entry.after || []).forEach(function(config) {
          const node = clonePromo(config);
          if (!node) return;
          if (!insertsAfter[productIndex1]) insertsAfter[productIndex1] = [];
          insertsAfter[productIndex1].push(node);
        });
      });

      trailing.forEach(function(config) {
        const node = clonePromo(config);
        if (node) trailingNodes.push(node);
      });

      if (!Object.keys(insertsBefore).length && !Object.keys(insertsAfter).length && !trailingNodes.length) return;

      existingPromos.forEach(function(el) {
        el.remove();
      });

      Object.keys(insertsBefore).map(Number).sort(function(a, b) { return b - a; }).forEach(function(productIndex1) {
        const product = products[productIndex1 - 1];
        if (!product) return;
        const nodes = insertsBefore[productIndex1];
        for (let i = nodes.length - 1; i >= 0; i--) {
          product.before(nodes[i]);
        }
      });

      Object.keys(insertsAfter).map(Number).sort(function(a, b) { return a - b; }).forEach(function(productIndex1) {
        const product = products[productIndex1 - 1];
        if (!product) return;
        const nodes = insertsAfter[productIndex1];
        let anchor = product;
        for (let i = 0; i < nodes.length; i++) {
          anchor.after(nodes[i]);
          anchor = nodes[i];
        }
      });

      trailingNodes.forEach(function(node) {
        this.grid.appendChild(node);
      }.bind(this));
    }
    getPageFromUrl(url) {
      if (!url) return null;
      const match = url.match(/[?&]page=(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    }
    buildPageUrl(pageNumber) {
      const prevI = this.i;
      this.i = pageNumber;
      const url = document.location.pathname + this.addUrlParam(document.location.search, 'page');
      this.i = prevI;
      return url;
    }
    connectedCallback() {
      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }
      
      // Check if there are more products available on initial load
      // Wait for initial fill script to complete (it sets window.__fillingInitialRow to false)
      const checkInitialProducts = () => {
        if (window.__fillingInitialRow) {
          setTimeout(checkInitialProducts, 200);
          return;
        }
        setTimeout(() => {
          this.checkIfMoreProductsAvailable();
        }, 500);
      };
      setTimeout(checkInitialProducts, 500);
    }
    addUrlParam(search, key) {
      var newParam = key + '=' + this.i,
        params = '?' + newParam;

      // If the "search" string exists, then build params from it
      if (search) {
        // Try to replace an existance instance
        params = search.replace(new RegExp('([?&])' + key + '[^&]*'), '$1' + newParam);

        // If nothing was replaced, then add the new param to the end
        if (params === search) {
          params += '&' + newParam;
        }
      }

      return params;
    }
    loadMore() {
      let base = this;
      this.button.addEventListener('click', function() {
        // Load the current page
        base.lastLoadTriggeredByButton = true;
        base.loadProducts();
        this.blur();
        return false;
      });
    }
    infinite() {
      let base = this;
      base.observer = new IntersectionObserver(function(entries) {
        if (entries[0].intersectionRatio === 1) {
          base.loadProducts();
        }
      }, {
        threshold: [0, 1]
      });
      base.observer.observe(base);
    }
    loadProducts(onlyFillRow = false) {
      let base = this;
      if (base.getAttribute('loading')) {
        return;
      }
      
      // If all products are already loaded, don't load anything
      if (base.allProductsLoaded && !onlyFillRow) {
        console.log('[Pagination] All products already loaded - skipping load');
        base.hideLoadMoreButton();
        return;
      }
      
      let pageToLoad;
      let url;
      
      if (onlyFillRow) {
        if (base.nextUrl) {
          url = base.nextUrl;
          pageToLoad = base.getPageFromUrl(url) || (base.i + 1);
        } else {
          pageToLoad = (base.currentPage || base.i) + 1;
          url = base.buildPageUrl(pageToLoad);
        }
        base.partialPageLoaded = pageToLoad;
      } else {
        const pendingPartialPage = (base.partialPageLoaded && base.pageSkipCount[base.partialPageLoaded] > 0)
          ? base.partialPageLoaded
          : (window.__partialPageLoaded && window.__pageSkipCount && window.__pageSkipCount[window.__partialPageLoaded] > 0)
            ? window.__partialPageLoaded
            : null;
        if (pendingPartialPage) {
          pageToLoad = pendingPartialPage;
          url = base.buildPageUrl(pageToLoad);
          base.i = pageToLoad;
          if (window.__pageSkipCount && window.__pageSkipCount[pendingPartialPage]) {
            base.pageSkipCount[pendingPartialPage] = window.__pageSkipCount[pendingPartialPage];
            delete window.__pageSkipCount[pendingPartialPage];
          }
          if (window.__preloadedProducts) {
            window.__preloadedProducts.forEach(function(id) {
              base.preloadedProducts.add(id);
            });
          }
        } else {
        // Normal load - Prefer nextUrl if available
        if (base.nextUrl) {
           url = base.nextUrl;
           pageToLoad = base.getPageFromUrl(url);
           if (pageToLoad) {
             base.i = pageToLoad; // Sync i with the page we are loading
           } else {
             // Fallback if no page param in nextUrl
             base.i++;
             pageToLoad = base.i;
           }
        } else {
          // Fallback to manual construction if nextUrl is missing (should not happen if liquid is correct)
          // Check if we have a partially loaded page to continue from
          if (base.partialPageLoaded && base.pageSkipCount[base.partialPageLoaded] > 0) {
            pageToLoad = base.partialPageLoaded;
            // After loading, i will point to the next page
          } else if (window.__pageSkipCount && window.__partialPageLoaded && window.__pageSkipCount[window.__partialPageLoaded] > 0) {
            // Check global partial page
            pageToLoad = window.__partialPageLoaded;
            // Merge into instance
            base.partialPageLoaded = pageToLoad;
            base.pageSkipCount[pageToLoad] = window.__pageSkipCount[pageToLoad];
            // Merge global preloadedProducts into instance
            if (window.__preloadedProducts) {
              window.__preloadedProducts.forEach(function(id) {
                base.preloadedProducts.add(id);
              });
            }
          } else {
            // No partial page, increment to next page
            base.i++;
            pageToLoad = base.i;
          }
          
          // Build URL manually
          const originalI = base.i;
          base.i = pageToLoad;
          url = document.location.pathname + base.addUrlParam(document.location.search, 'page');
          
          // Restore i if needed (logic from before)
          if (base.partialPageLoaded && pageToLoad === base.partialPageLoaded) {
             base.i = base.partialPageLoaded + 1;
          }
        }
        }
      }
      
      console.log('[Pagination] Loading page', pageToLoad, 'from URL:', url);
      
      base.setAttribute('loading', true);
      
      fetch(url)
        .then(response => response.text())
        .then((responseText) => {
          const html = responseText;
          
          // Update current page after loading
          if (!onlyFillRow) {
            base.currentPage = pageToLoad;
            console.log('[Pagination] Loaded page', pageToLoad, '- currentPage updated to', base.currentPage, '/', base.totalPages);
          }
          
          base.renderProducts(html, url, onlyFillRow, pageToLoad);

          if (!onlyFillRow) {
            dispatchCustomEvent('pagination:page-changed', {
              url: url
            });
            //SWYM code
            document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
            //added by Jumpstart
            updateGridHeightcollection();
          }
        })
        .catch(error => {
           console.error('[Pagination] Error loading products:', error);
           base.removeAttribute('loading');
        });
    }
    renderProducts(html, url, onlyFillRow = false, loadedPageNumber = null) {
      let base = this;
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const grid_to_replace = doc.getElementById('product-grid');
      
      // Extract nextUrl from the response
      const paginationTheme = doc.querySelector('pagination-theme');
      if (paginationTheme && paginationTheme.hasAttribute('data-next-url')) {
        base.nextUrl = paginationTheme.getAttribute('data-next-url');
        console.log('[Pagination] Updated nextUrl to:', base.nextUrl);
      } else {
        const expectedNextPage = (loadedPageNumber !== null ? loadedPageNumber + 1 : base.currentPage + 1);
        if (base.totalPages && expectedNextPage <= base.totalPages) {
          base.nextUrl = base.buildPageUrl(expectedNextPage);
          console.log('[Pagination] Built fallback nextUrl:', base.nextUrl);
        } else if (!onlyFillRow) {
          base.nextUrl = null;
          base.allProductsLoaded = true;
          base.hideLoadMoreButton();
        }
      }

      if (!grid_to_replace) {
        base.removeAttribute('loading');
        base.hideLoadMoreButton();
        return;
      }
      let products = grid_to_replace.querySelectorAll('.column');
      
      // Get the page number that was loaded - use the passed parameter if available
      const currentPage = loadedPageNumber !== null ? loadedPageNumber : (onlyFillRow ? (base.partialPageLoaded || base.i) : base.i);
      
      // Merge global preloadedProducts into instance BEFORE checking for duplicates
      if (window.__preloadedProducts) {
        window.__preloadedProducts.forEach(function(id) {
          base.preloadedProducts.add(id);
        });
      }
      
      // Get all existing product IDs in the DOM to prevent duplicates
      const existingProductIds = new Set();
      if (base.grid) {
        const existingProducts = base.grid.querySelectorAll('.column');
        existingProducts.forEach(function(existingProduct) {
          const existingId = base.getProductId(existingProduct);
          if (existingId) {
            existingProductIds.add(existingId);
          }
        });
      }
      
      // If only filling row, limit to the number needed
      let productsToAdd = products;
      let skipCount = 0;
      
      if (onlyFillRow) {
        const needed = base.getProductsNeededToFillRow();
        if (needed > 0 && products.length > 0) {
          // Only take product cards needed to fill the visual row
          productsToAdd = Array.from(products)
            .filter(function(el) { return !el.classList.contains('promotion-column'); })
            .slice(0, needed);
          base.partialPageLoaded = currentPage;
        } else {
          base.removeAttribute('loading');
          base.fillingRow = false;
          // Reset global flag if we were filling initial row
          if (window.__fillingInitialRow) {
            window.__fillingInitialRow = false;
          }
          return;
        }
      } else {
        // Normal load - skip products that were pre-loaded from this page
        
        // Check instance skip count first
        if (base.pageSkipCount[currentPage] && base.pageSkipCount[currentPage] > 0) {
          skipCount = base.pageSkipCount[currentPage];
          // Clear the skip count for this page (but keep partialPageLoaded to verify all products are loaded)
          delete base.pageSkipCount[currentPage];
        }
        
        // Also check global preloadedProducts (from initial Liquid script)
        // Use the larger skip count if both exist (should be the same, but be safe)
        if (window.__pageSkipCount && window.__pageSkipCount[currentPage]) {
          const globalSkipCount = window.__pageSkipCount[currentPage];
          if (globalSkipCount > skipCount) {
            skipCount = globalSkipCount;
          }
          delete window.__pageSkipCount[currentPage];
        }
        
        // Apply skip count once
        if (skipCount > 0) {
          productsToAdd = Array.from(products).slice(skipCount);
        }
        
      }

      // Filter out duplicates before appending
      const productsToAppend = [];
      for (var i = 0; i < productsToAdd.length; i++) {
        const item = productsToAdd[i];
        if (item.classList.contains('promotion-column')) {
          productsToAppend.push(item);
          continue;
        }

        const productId = base.getProductId(item);
        
        // Check if product already exists in DOM or was pre-loaded
        const isDuplicate = (productId && existingProductIds.has(productId)) ||
                           (productId && base.preloadedProducts.has(productId)) ||
                           (window.__preloadedProducts && productId && window.__preloadedProducts.has(productId));
        
        if (!isDuplicate) {
          productsToAppend.push(item);
          // Add to tracking sets
          if (productId) {
            existingProductIds.add(productId);
            base.preloadedProducts.add(productId);
          }
        }
      }
      
      // For normal loads (not onlyFillRow), ensure visual grid slots (products + promos) fill complete rows
      if (!onlyFillRow) {
        const columns = base.getColumnsDesktop();
        const currentProductCount = base.getCurrentProductCount();
        let addingProductCount = 0;
        for (var p = 0; p < productsToAppend.length; p++) {
          if (!productsToAppend[p].classList.contains('promotion-column')) {
            addingProductCount++;
          }
        }
        let totalProductsAfterLoad = currentProductCount + addingProductCount;
        let totalSlotsAfterLoad = base.getGridSlotCountForProducts(totalProductsAfterLoad);
        let remainder = totalSlotsAfterLoad % columns;
        
        if (remainder !== 0) {
          // Find how many product cards are needed for a complete visual row
          let productsNeeded = 0;
          for (let add = 1; add <= columns * 4; add++) {
            if (base.getGridSlotCountForProducts(totalProductsAfterLoad + add) % columns === 0) {
              productsNeeded = add;
              break;
            }
          }
          console.log('[Pagination] After loading, slots will be', totalSlotsAfterLoad, '- need', productsNeeded, 'more products to complete row');
          
          // Try to get more products from the current page (if available)
          const remainingProducts = Array.from(productsToAdd).filter(function(el) {
            return !el.classList.contains('promotion-column') && !productsToAppend.includes(el);
          });
          let additionalProducts = [];
          
          for (var k = 0; k < remainingProducts.length && additionalProducts.length < productsNeeded; k++) {
            const productId = base.getProductId(remainingProducts[k]);
            const isDuplicate = (productId && existingProductIds.has(productId)) ||
                               (productId && base.preloadedProducts.has(productId)) ||
                               (window.__preloadedProducts && productId && window.__preloadedProducts.has(productId));
            
            if (!isDuplicate) {
              additionalProducts.push(remainingProducts[k]);
              if (productId) {
                existingProductIds.add(productId);
                base.preloadedProducts.add(productId);
              }
            }
          }
          
          // If we got enough from current page, add them
          if (additionalProducts.length >= productsNeeded) {
            productsToAppend.push(...additionalProducts.slice(0, productsNeeded));
            console.log('[Pagination] Added', productsNeeded, 'more products from current page to complete row');
          } else if (additionalProducts.length > 0) {
            // Add what we have, then we'll need to fetch more from next page
            productsToAppend.push(...additionalProducts);
            console.log('[Pagination] Added', additionalProducts.length, 'products from current page, will fetch', productsNeeded - additionalProducts.length, 'more from next page');
            
            // Mark that we need to fetch more products after this load
            base.productsNeededToCompleteRow = Math.max(base.productsNeededToCompleteRow || 0, productsNeeded - additionalProducts.length);
          } else {
            // No more products on current page, mark that we need to fetch from next page
            base.productsNeededToCompleteRow = Math.max(base.productsNeededToCompleteRow || 0, productsNeeded);
            console.log('[Pagination] No more products on current page, will fetch', productsNeeded, 'more from next page after this load');
          }
        } else {
          console.log('[Pagination] After loading, slots will be', totalSlotsAfterLoad, '- already a complete row');
        }
      }
      
      // Append all non-duplicate products
      for (var j = 0; j < productsToAppend.length; j++) {
        this.grid.appendChild(productsToAppend[j]);
      }
      if (productsToAppend.length && typeof initswatchflickity === 'function') {
        initswatchflickity();
      }

      // Only rebalance promos on normal load-more, not during row-fill.
      // Row-fill runs right after Liquid SSR and rebuilding makes promos appear to swap.
      if (!onlyFillRow) {
        base.repositionPromotionColumns();
      }

      // After promos are placed, re-check visual row completeness
      if (!base.allProductsLoaded) {
        const remainingAfterPromo = base.getProductsNeededToFillRow();
        if (remainingAfterPromo > 0) {
          base.productsNeededToCompleteRow = Math.max(base.productsNeededToCompleteRow || 0, remainingAfterPromo);
        }
      }

      if (onlyFillRow && productsToAppend.length > 0) {
        base.pageSkipCount[currentPage] = (base.pageSkipCount[currentPage] || 0) + productsToAppend.length;
      }
      
      base.removeAttribute('loading');
      base.fillingRow = false;
      // Reset global flag if we were filling initial row
      if (window.__fillingInitialRow) {
        window.__fillingInitialRow = false;
      }

      if (onlyFillRow) {
        const remainingNeeded = base.getProductsNeededToFillRow();
        if (remainingNeeded > 0 && !base.allProductsLoaded && base.nextUrl) {
          requestAnimationFrame(() => {
            base.loadProducts(true);
          });
          return;
        } else {
          base.lastLoadTriggeredByButton = false;
        }
      }
      
      if (!onlyFillRow && base.productsNeededToCompleteRow > 0 && !base.allProductsLoaded) {
        const canPreloadMore = base.autoPreloadCount < base.autoPreloadLimit;
        const preferPartialFill = base.lastLoadTriggeredByButton;
        base.productsNeededToCompleteRow = 0;
        if (preferPartialFill) {
          requestAnimationFrame(() => {
            base.loadProducts(true);
          });
        } else if (canPreloadMore) {
          base.autoPreloadCount++;
          requestAnimationFrame(() => {
            base.loadProducts(false);
          });
        } else {
          requestAnimationFrame(() => {
            base.loadProducts(true);
          });
        }
      } else {
        // Normal completion checks
        base.completeCallback(productsToAppend.length, onlyFillRow);
      }
    }
    
    // Updated completeCallback to be simpler and rely on nextUrl
    completeCallback(productsAppended, onlyFillRow) {
      let base = this;
      
      // If we used nextUrl and it's now null, we are done.
      if (base.nextUrl === null) {
        base.allProductsLoaded = true;
        base.hideLoadMoreButton();
        return;
      }
      
      // If no products were appended (all duplicates), check if more products available
      if (productsAppended === 0 && !onlyFillRow) {
        base.checkIfMoreProductsAvailable();
      }
    }
    
    checkIfMoreProductsAvailable() {
      let base = this;
      if (base.nextUrl) {
         // We have a next URL, so more products are likely available.
         // Do nothing, button stays visible.
      } else {
         // No next URL, hide button.
         base.hideLoadMoreButton();
      }
    }
    
    hideLoadMoreButton() {
      if (this.button) {
        this.button.classList.add('hidden');
        this.button.style.display = 'none';
      }
      // Also hide any loading overlay in the pagination container
      const overlay = this.querySelector('.loading-overlay');
      if (overlay) {
        overlay.style.display = 'none';
      }
    }
  }
  customElements.define('pagination-theme', Pagination);
}
